import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { MicrophoneIcon, StopCircleIcon, SpeakerWaveIcon, PauseIcon, StopIcon } from '@heroicons/react/24/solid';
import { DefaultService } from '../../generated-api';
import { useAudioRecorder } from '../../hooks/useAudioRecorder';

interface ChatWidgetProps {
  gameId: string;
  gameName: string;
  hasRules: boolean;
}

interface ChatMessage {
  text: string;
  sender: 'user' | 'bot';
  audioBase64?: string;
}

const STORAGE_KEY_PREFIX = 'chat_history_';

const ChatWidget: React.FC<ChatWidgetProps> = ({ gameId, gameName, hasRules }) => {
  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    try {
      const stored = localStorage.getItem(`${STORAGE_KEY_PREFIX}${gameId}`);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isProcessingVoice, setIsProcessingVoice] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const {
    isRecording,
    isSupported: micSupported,
    error: micError,
    startRecording,
    stopRecording,
    clearError: clearMicError,
  } = useAudioRecorder();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Persist messages to localStorage (strip audioBase64 to avoid bloating)
  useEffect(() => {
    try {
      const messagesToStore = messages.map(({ audioBase64: _audio, ...rest }) => rest);
      localStorage.setItem(`${STORAGE_KEY_PREFIX}${gameId}`, JSON.stringify(messagesToStore));
    } catch {
      // localStorage full or unavailable
    }
  }, [messages, gameId]);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playingAudioIndex, setPlayingAudioIndex] = useState<number | null>(null);
  const [audioPaused, setAudioPaused] = useState(false);

  const cleanupAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      if (audioRef.current.src.startsWith('blob:')) {
        URL.revokeObjectURL(audioRef.current.src);
      }
      audioRef.current = null;
    }
  }, []);

  const stopAudio = useCallback(() => {
    cleanupAudio();
    setPlayingAudioIndex(null);
    setAudioPaused(false);
  }, [cleanupAudio]);

  const playAudio = useCallback((base64: string, messageIndex: number) => {
    cleanupAudio();

    const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
    const blob = new Blob([bytes], { type: 'audio/mpeg' });
    const audio = new Audio(URL.createObjectURL(blob));
    audioRef.current = audio;
    setPlayingAudioIndex(messageIndex);
    setAudioPaused(false);

    audio.onended = () => {
      if (audio.src.startsWith('blob:')) {
        URL.revokeObjectURL(audio.src);
      }
      audioRef.current = null;
      setPlayingAudioIndex(null);
      setAudioPaused(false);
    };

    audio.play().catch(() => {
      setPlayingAudioIndex(null);
    });
  }, [cleanupAudio]);

  const togglePauseAudio = useCallback(() => {
    if (!audioRef.current) return;
    if (audioRef.current.paused) {
      audioRef.current.play();
      setAudioPaused(false);
    } else {
      audioRef.current.pause();
      setAudioPaused(true);
    }
  }, []);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || !hasRules) return;

    const userMessage: ChatMessage = {
      text: inputValue,
      sender: 'user',
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    try {
      const response = await DefaultService.sendChatMessageGamesGameIdChatPost({
        gameId,
        requestBody: {
          message: inputValue,
          conversationHistory: messages.map(msg => ({
            text: msg.text,
            sender: msg.sender,
          })),
        },
      });

      const botMessage: ChatMessage = {
        text: response.response,
        sender: 'bot',
      };

      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage: ChatMessage = {
        text: 'Sorry, I encountered an error. Please try again.',
        sender: 'bot',
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleVoiceSend = async (audioBlob: Blob) => {
    setIsProcessingVoice(true);

    try {
      const response = await DefaultService.voiceChatGamesGameIdVoiceChatPost({
        gameId,
        formData: {
          audio: audioBlob,
          conversation_history: JSON.stringify(
            messages.map(m => ({ text: m.text, sender: m.sender }))
          ),
        },
      });

      // Add user message (transcription) and bot message with audio
      const botIndex = messages.length + 1; // user msg at messages.length, bot at messages.length + 1
      setMessages(prev => [
        ...prev,
        { text: response.transcription, sender: 'user' },
        {
          text: response.response,
          sender: 'bot',
          audioBase64: response.audio_base64 ?? undefined,
        },
      ]);

      // Auto-play TTS response
      if (response.audio_base64) {
        playAudio(response.audio_base64, botIndex);
      }
    } catch (error) {
      console.error('Error sending voice message:', error);
      setMessages(prev => [
        ...prev,
        { text: 'Sorry, I encountered an error processing your voice message. Please try again.', sender: 'bot' },
      ]);
    } finally {
      setIsProcessingVoice(false);
    }
  };

  const handleMicToggle = async () => {
    if (isRecording) {
      const blob = await stopRecording();
      if (blob) {
        handleVoiceSend(blob);
      }
    } else {
      clearMicError();
      await startRecording();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleClearHistory = () => {
    setMessages([]);
    localStorage.removeItem(`${STORAGE_KEY_PREFIX}${gameId}`);
  };

  const isBusy = isTyping || isProcessingVoice;

  if (!hasRules) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-disabled mb-4">
            <svg
              className="mx-auto h-16 w-16"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-heading mb-2">Chat is disabled</h3>
          <p className="text-muted">
            Upload a PDF rulebook to enable the AI chat assistant
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Header */}
      <div className="flex items-center justify-between pb-3 border-b border-edge mb-0">
        <h2 className="text-xl font-bold text-heading">Ask about {gameName}</h2>
        {messages.length > 0 && (
          <button
            onClick={handleClearHistory}
            className="text-xs text-disabled hover:text-red-500 transition-colors"
          >
            Clear history
          </button>
        )}
      </div>

      {/* Messages area — fills remaining space */}
      <div className="flex-1 overflow-y-auto py-4 space-y-4 min-h-0">
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-disabled">
            <div className="text-center">
              <p className="text-lg mb-2">Ask me anything about {gameName}&apos;s rules, strategy, or setup!</p>
              <p className="text-sm text-faint">Your conversation is saved locally</p>
            </div>
          </div>
        ) : (
          <>
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] lg:max-w-[70%] px-4 py-3 rounded-2xl ${
                    message.sender === 'user'
                      ? 'bg-primary-700 text-white rounded-br-md'
                      : 'bg-surface-subtle text-heading rounded-bl-md'
                  }`}
                >
                  {message.sender === 'user' ? (
                    <p className="text-sm">{message.text}</p>
                  ) : (
                    <div>
                      <div className="prose prose-sm max-w-none prose-headings:text-heading prose-p:text-heading prose-strong:text-heading prose-ul:text-heading prose-ol:text-heading prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {message.text}
                        </ReactMarkdown>
                      </div>
                      {message.audioBase64 && (
                        <div className="mt-2 inline-flex items-center gap-1">
                          {playingAudioIndex === index ? (
                            <>
                              <button
                                onClick={togglePauseAudio}
                                className="inline-flex items-center gap-1 text-xs text-primary-700 hover:text-primary-800 transition-colors"
                                title={audioPaused ? 'Resume' : 'Pause'}
                              >
                                {audioPaused ? (
                                  <SpeakerWaveIcon className="h-4 w-4" />
                                ) : (
                                  <PauseIcon className="h-4 w-4" />
                                )}
                                <span>{audioPaused ? 'Resume' : 'Pause'}</span>
                              </button>
                              <button
                                onClick={stopAudio}
                                className="inline-flex items-center gap-1 text-xs text-muted hover:text-red-500 transition-colors ml-2"
                                title="Stop"
                              >
                                <StopIcon className="h-4 w-4" />
                                <span>Stop</span>
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => playAudio(message.audioBase64!, index)}
                              className="inline-flex items-center gap-1 text-xs text-muted hover:text-heading transition-colors"
                              title="Play audio"
                            >
                              <SpeakerWaveIcon className="h-4 w-4" />
                              <span>Play audio</span>
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {isBusy && (
              <div className="flex justify-start">
                <div className="bg-surface-subtle rounded-2xl rounded-bl-md px-4 py-3">
                  <div className="flex space-x-2">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Mic error */}
      {micError && (
        <div className="px-3 py-2 text-xs text-red-600 bg-red-50 rounded-lg">
          {micError}
        </div>
      )}

      {/* Input — pinned at bottom */}
      <div className="pt-3 border-t border-edge">
        <div className="flex gap-2">
          <input
            type="text"
            value={isRecording ? '' : inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={isRecording ? 'Listening...' : 'Type your question...'}
            className="flex-1 px-4 py-3 border border-edge-input rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
            disabled={!hasRules || isRecording}
          />
          {micSupported && (
            <button
              onClick={handleMicToggle}
              disabled={isBusy || !hasRules}
              className={`
                relative px-3 py-3 rounded-xl font-medium transition-colors text-sm
                ${isRecording
                  ? 'bg-red-500 text-white hover:bg-red-600'
                  : isBusy || !hasRules
                    ? 'bg-surface-skeleton text-disabled cursor-not-allowed'
                    : 'bg-surface-subtle text-heading hover:bg-surface-skeleton'
                }
              `}
              title={isRecording ? 'Stop recording' : 'Start voice input'}
            >
              {isRecording ? (
                <>
                  <span className="absolute -top-1 -right-1 flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                  </span>
                  <StopCircleIcon className="h-5 w-5" />
                </>
              ) : (
                <MicrophoneIcon className="h-5 w-5" />
              )}
            </button>
          )}
          <button
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || !hasRules || isBusy || isRecording}
            className={`
              px-6 py-3 rounded-xl font-medium transition-colors text-sm
              ${inputValue.trim() && hasRules && !isBusy && !isRecording
                ? 'bg-primary-700 text-white hover:bg-primary-800'
                : 'bg-surface-skeleton text-disabled cursor-not-allowed'
              }
            `}
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatWidget;
